import {useAuth} from "@/contexts/AuthContext.tsx";
import {useQuery} from "@tanstack/react-query";
import {UserRole} from "@/types/UserRole.ts";
import {Setting} from "@/types/Setting.ts";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getSettings} from "@/services/settingsService.ts";
import {getEffectiveDeadlines} from "@/services/deadlineExtensionService.ts";
import AdminDashboardPage from "@/pages/dashboard/admin";
import MentorDashboardPage from "@/pages/dashboard/mentor";
import MenteeDashboardPage from "@/pages/dashboard/mentee";
import {getPreferenceCount} from "@/services/preferenceService.ts";
import {isPreferenceSubmissionPeriod} from "@/utils/time-helper.ts";

const DashboardPage = () => {
    const {loggedInUser: user} = useAuth();

    const {data: settings, isFetching: isFetchingSettings} = useQuery({
        queryKey: queryKeys.fetchSettings.all,
        queryFn: () => getSettings()
    });

    const getSettingByKey = (settings: Setting[] | undefined, key: string): string | undefined => {
        return settings?.find(setting => setting.key === key)?.value;
    };

    const parseDate = (dateString: string | undefined): Date | undefined => {
        return dateString ? new Date(dateString) : undefined;
    };

    const parseNumber = (numString: string | undefined): number => {
        return numString ? parseInt(numString) : -1;
    };

    const maxNumberOfMentees = parseNumber(getSettingByKey(settings, 'max_number_of_mentees'));
    const globalSubmitPreferenceDeadline = parseDate(getSettingByKey(settings, 'last_date_to_submit_preferences'));
    const globalSelectMentorDeadline = parseDate(getSettingByKey(settings, 'last_date_to_choose_mentor'));

    const {data: effectiveDeadlines} = useQuery({
        queryKey: queryKeys.effectiveDeadlines,
        queryFn: getEffectiveDeadlines,
        enabled: !!user?.id,
    });

    const submitPreferenceDeadline =
        effectiveDeadlines?.preferenceSubmission
            ? new Date(effectiveDeadlines.preferenceSubmission)
            : globalSubmitPreferenceDeadline;
    const selectMentorDeadline =
        effectiveDeadlines?.mentorSelection
            ? new Date(effectiveDeadlines.mentorSelection)
            : globalSelectMentorDeadline;

    const {isFetching: isFetchingPreferenceCount} = useQuery({
        queryKey: queryKeys.fetchPreferences.count,
        queryFn: () => getPreferenceCount(),
        initialData: 0,
        enabled: isPreferenceSubmissionPeriod(submitPreferenceDeadline),
    });

    if (isFetchingSettings || isFetchingPreferenceCount) {
        return (
            <div className="flex items-center justify-center min-h-screen p-5 min-w-screen">
                <div className="flex space-x-2 animate-pulse">
                    <div className="w-3 h-3 bg-zinc-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-zinc-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-zinc-500 rounded-full"></div>
                </div>
            </div>
        );
    }

    if (!user) {
        return null;
    }

    switch (user.role) {
        case UserRole.ADMIN:
            return (
                <AdminDashboardPage
                    submitPreferenceDeadline={submitPreferenceDeadline}
                    selectMentorDeadline={selectMentorDeadline}
                />
            );
        case UserRole.MENTOR:
            return (
                <MentorDashboardPage
                    mentorId={user.mentor?.id ?? -1}
                    submitPreferenceDeadline={submitPreferenceDeadline}
                    selectMentorDeadline={selectMentorDeadline}
                />
            );
        case UserRole.MENTEE:
            return (
                <MenteeDashboardPage
                    menteeId={user.mentee?.id ?? -1}
                    maxNumberOfMentees={maxNumberOfMentees}
                    submitPreferenceDeadline={submitPreferenceDeadline}
                    selectMentorDeadline={selectMentorDeadline}
                />
            );
        default:
            return null;
    }
}

export default DashboardPage;
